import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Gift, Sparkles } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "../hooks/use-toast";
import { useLang } from "../i18n/LangContext";
import useUTM from "../hooks/useUTM";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const I18N = {
  uk: {
    badge: "БЕЗКОШТОВНИЙ ВЕБІНАР",
    title: "Зробіть перший крок до навчання за кордоном",
    subtitle: "Зареєструйтеся на безкоштовний вебінар, поставте свої запитання експертам та отримайте ваучер 100 €*.",
    name: "Ім'я", email: "E-mail", phone: "Телефон",
    submit: "Зареєструватися на вебінар",
    submitting: "Відправляємо...",
    disclaimer: "*Ваучер 100 € діє на будь-який пакет послуг STUDYGRI: вступ до університету, школа-пансіон, Foundation або мовний курс.",
    err: "Заповніть ім'я та email",
    errSubmit: "Спробуйте ще раз",
    sent: "Дякуємо за реєстрацію!",
    sentDesc: "Менеджер зв'яжеться з вами найближчим часом і надішле деталі вебінару."
  },
  ru: {
    badge: "БЕСПЛАТНЫЙ ВЕБИНАР",
    title: "Сделайте первый шаг к учёбе за границей",
    subtitle: "Зарегистрируйтесь на бесплатный вебинар, задайте свои вопросы экспертам и получите ваучер 100 €*.",
    name: "Имя", email: "E-mail", phone: "Телефон",
    submit: "Зарегистрироваться на вебинар",
    submitting: "Отправляем...",
    disclaimer: "*Ваучер 100 € действует на любой пакет услуг STUDYGRI: поступление в университет, школа-пансион, Foundation или языковой курс.",
    err: "Заполните имя и email",
    errSubmit: "Попробуйте ещё раз",
    sent: "Спасибо за регистрацию!",
    sentDesc: "Менеджер свяжется с вами в ближайшее время и вышлет детали вебинара."
  },
  en: {
    badge: "FREE WEBINAR",
    title: "Take the first step toward studying abroad",
    subtitle: "Register for our free webinar, ask experts your questions and receive a €100 voucher*.",
    name: "Name", email: "E-mail", phone: "Phone",
    submit: "Register for the webinar",
    submitting: "Sending...",
    disclaimer: "*The €100 voucher is valid for any STUDYGRI service package: university admission, boarding school, Foundation or language course.",
    err: "Fill in name and email",
    errSubmit: "Please try again",
    sent: "Thanks for registering!",
    sentDesc: "Our manager will contact you shortly with webinar details."
  },
  pl: {
    badge: "BEZPŁATNY WEBINAR",
    title: "Zrób pierwszy krok w kierunku studiów za granicą",
    subtitle: "Zapisz się na bezpłatny webinar, zadaj pytania ekspertom i odbierz voucher 100 €*.",
    name: "Imię", email: "E-mail", phone: "Telefon",
    submit: "Zapisz się na webinar",
    submitting: "Wysyłamy...",
    disclaimer: "*Voucher 100 € obowiązuje na dowolny pakiet STUDYGRI: rekrutacja na uczelnię, szkoła z internatem, Foundation lub kurs językowy.",
    err: "Uzupełnij imię i email",
    errSubmit: "Spróbuj ponownie",
    sent: "Dziękujemy za rejestrację!",
    sentDesc: "Nasz menedżer wkrótce się z Tobą skontaktuje i prześle szczegóły webinaru."
  },
  it: {
    badge: "WEBINAR GRATUITO",
    title: "Fai il primo passo verso lo studio all'estero",
    subtitle: "Iscriviti al webinar gratuito, fai le tue domande agli esperti e ricevi un voucher da 100 €*.",
    name: "Nome", email: "E-mail", phone: "Telefono",
    submit: "Iscriviti al webinar",
    submitting: "Invio...",
    disclaimer: "*Il voucher da 100 € è valido su qualsiasi pacchetto STUDYGRI: ammissione universitaria, collegio, Foundation o corso di lingua.",
    err: "Inserisci nome ed email",
    errSubmit: "Riprova",
    sent: "Grazie per la registrazione!",
    sentDesc: "Il nostro manager ti contatterà a breve con i dettagli del webinar."
  },
  es: {
    badge: "WEBINAR GRATUITO",
    title: "Da el primer paso hacia estudiar en el extranjero",
    subtitle: "Regístrate en el webinar gratuito, haz tus preguntas a los expertos y recibe un vale de 100 €*.",
    name: "Nombre", email: "E-mail", phone: "Teléfono",
    submit: "Registrarme en el webinar",
    submitting: "Enviando...",
    disclaimer: "*El vale de 100 € es válido para cualquier paquete STUDYGRI: admisión universitaria, internado, Foundation o curso de idiomas.",
    err: "Rellena nombre y email",
    errSubmit: "Inténtalo de nuevo",
    sent: "¡Gracias por registrarte!",
    sentDesc: "Nuestro gestor te contactará pronto con los detalles del webinar."
  },
  ar: {
    badge: "ندوة مجانية عبر الإنترنت",
    title: "اتخذ خطوتك الأولى نحو الدراسة في الخارج",
    subtitle: "سجّل في الندوة المجانية، اطرح أسئلتك على الخبراء واحصل على قسيمة بقيمة 100 يورو*.",
    name: "الاسم", email: "البريد الإلكتروني", phone: "الهاتف",
    submit: "التسجيل في الندوة",
    submitting: "جارٍ الإرسال...",
    disclaimer: "*قسيمة 100 يورو صالحة لأي باقة من باقات STUDYGRI: قبول جامعي، مدرسة داخلية، Foundation أو دورة لغة.",
    err: "املأ الاسم والبريد الإلكتروني",
    errSubmit: "حاول مرة أخرى",
    sent: "شكراً لتسجيلك!",
    sentDesc: "سيتواصل معك مديرنا قريباً بتفاصيل الندوة."
  },
  zh: {
    badge: "免费网络研讨会",
    title: "迈出海外求学的第一步",
    subtitle: "报名参加免费网络研讨会，向专家提问并获得100欧元代金券*。",
    name: "姓名", email: "电子邮箱", phone: "电话",
    submit: "报名网络研讨会",
    submitting: "提交中...",
    disclaimer: "*100欧元代金券适用于任何STUDYGRI服务套餐：大学申请、寄宿学校、Foundation 或语言课程。",
    err: "请填写姓名和邮箱",
    errSubmit: "请重试",
    sent: "感谢您的注册！",
    sentDesc: "我们的顾问将很快与您联系并发送研讨会详情。"
  }
};

const WebinarSignup = () => {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.uk;
  const isRtl = lang === 'ar';
  const utm = useUTM();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast({ title: t.err, variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name, phone, email,
        country: "", program: "Webinar",
        message: "Webinar signup",
        lang, source: "webinar-signup",
        ...utm
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'webinar_registration_success', form_source: 'webinar-signup' });
      setSent(true);
      toast({ title: t.sent });
    } catch {
      toast({ title: t.errSubmit, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #1B2A4A 0%, #0F1A30 100%)', color: '#fff' }} dir={isRtl ? 'rtl' : 'ltr'} data-testid="webinar-signup-section">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(201,169,110,0.22) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(201,169,110,0.12) 0%, transparent 70%)' }} />

      <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
          {/* LEFT — Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold tracking-[0.22em] uppercase mb-6" style={{ background: 'rgba(201,169,110,0.14)', border: '1px solid rgba(201,169,110,0.45)', color: '#C9A96E' }}>
              <Sparkles className="w-3 h-3" />
              {t.badge}
            </div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-balance leading-[1.05]" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>
              {t.title}
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {t.subtitle}
            </p>
            {/* Voucher pill */}
            <div className="mt-7 inline-flex items-center gap-3 px-5 py-3 rounded-full" style={{ background: 'rgba(201,169,110,0.12)', border: '1px dashed rgba(201,169,110,0.55)' }}>
              <Gift className="w-5 h-5" style={{ color: '#C9A96E' }} />
              <span className="text-sm font-semibold" style={{ color: '#C9A96E' }}>Voucher 100 €</span>
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="relative">
            {!sent ? (
              <form onSubmit={submit} className="p-7 md:p-8 rounded-3xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,169,110,0.25)', backdropFilter: 'blur(12px)' }} data-testid="webinar-signup-form">
                <div className="space-y-3.5">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.name}
                    className="h-12 rounded-full px-5 text-white placeholder:text-white/50 border-white/20"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                    data-testid="webinar-signup-name"
                  />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.email}
                    className="h-12 rounded-full px-5 text-white placeholder:text-white/50 border-white/20"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                    data-testid="webinar-signup-email"
                  />
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t.phone}
                    className="h-12 rounded-full px-5 text-white placeholder:text-white/50 border-white/20"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                    data-testid="webinar-signup-phone"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full mt-5 h-13 py-4 rounded-full font-semibold text-base transition hover:opacity-90"
                  style={{ background: '#C9A96E', color: '#1B2A4A' }}
                  data-testid="webinar-signup-submit"
                >
                  {busy ? t.submitting : t.submit}
                </Button>
                <p className="mt-5 text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {t.disclaimer}
                </p>
              </form>
            ) : (
              <div className="p-8 md:p-10 rounded-3xl text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,169,110,0.35)', backdropFilter: 'blur(12px)' }} data-testid="webinar-signup-success">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)' }}>
                  <CheckCircle2 className="w-8 h-8" style={{ color: '#34D399' }} />
                </div>
                <h3 className="font-display text-2xl mb-2 uppercase">{t.sent}</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.sentDesc}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebinarSignup;
